import { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Loader2, Inbox, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useNoIndex } from "@/hooks/use-no-index";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const AdminLeads = () => {
  useNoIndex();
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchLeads = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/leads`);
      setLeads(data);
    } catch (e) {
      setError("Não foi possível carregar os leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-leads-page">
      <header className="border-b border-white/10 px-6 lg:px-10 h-[76px] flex items-center justify-between">
        <div>
          <h1 className="font-outfit font-semibold text-lg text-white">
            Painel de Leads
          </h1>
          <p className="text-xs text-white/40 font-manrope">
            {user?.email}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLeads}
            className="border-white/15 text-white hover:bg-white/10 rounded-full"
            data-testid="admin-refresh-button"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={logout}
            className="border-white/15 text-white hover:bg-white/10 rounded-full"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4 mr-1.5" />
            Sair
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        {loading ? (
          <div className="flex justify-center py-20" data-testid="admin-leads-loading">
            <Loader2 className="h-6 w-6 text-[#00D2D3] animate-spin" />
          </div>
        ) : error ? (
          <p className="text-red-400 text-sm" data-testid="admin-leads-error">
            {error}
          </p>
        ) : leads.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center text-center py-24 border border-dashed border-white/10 rounded-2xl"
            data-testid="admin-leads-empty-state"
          >
            <Inbox className="h-8 w-8 text-white/30 mb-4" />
            <p className="text-white/50 font-manrope text-sm">
              Nenhum lead recebido ainda.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-leads-table">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.02]">
                  <th className="px-5 py-3 font-outfit font-medium text-white/60">Nome</th>
                  <th className="px-5 py-3 font-outfit font-medium text-white/60">E-mail</th>
                  <th className="px-5 py-3 font-outfit font-medium text-white/60">WhatsApp</th>
                  <th className="px-5 py-3 font-outfit font-medium text-white/60">Mensagem</th>
                  <th className="px-5 py-3 font-outfit font-medium text-white/60">Data</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr
                    key={lead.id}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                    data-testid={`admin-lead-row-${lead.id}`}
                  >
                    <td className="px-5 py-4 text-white font-manrope">{lead.name}</td>
                    <td className="px-5 py-4 text-white/70 font-manrope">{lead.email || lead.contact || "—"}</td>
                    <td className="px-5 py-4 text-white/70 font-manrope">{lead.whatsapp || "—"}</td>
                    <td className="px-5 py-4 text-white/50 font-manrope max-w-xs truncate">
                      {lead.message || "—"}
                    </td>
                    <td className="px-5 py-4 text-white/40 font-manrope whitespace-nowrap">
                      {formatDate(lead.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminLeads;
